import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronLeft, 
  ChevronRight, 
  Edit, 
  Eye, 
  Plus,
  Trash,
  AlertCircle,
  Shield,
  Loader2,
  DollarSign,
  Clock
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { getApiUrl } from '../utils/api';
import { RootState } from '../store';

interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  basePrice: number;
  estimatedDuration: number;
  isActive: boolean;
  onlineBookingEnabled: boolean;
  depositRequired: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ServicesResponse {
  success: boolean;
  data: {
    services: Service[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

const categoryLabels: Record<string, string> = {
  'TATTOO': 'Tatouage',
  'PIERCING': 'Piercing',
  'CONSULTATION': 'Consultation',
  'AFTERCARE': 'Soins',
  'REMOVAL': 'Suppression',
  'JEWELRY': 'Bijouterie'
};

export function ServicesListPage() {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';
  
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; serviceIds: string[] }>({
    isOpen: false,
    serviceIds: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; serviceIds: string[] }>({
    isOpen: false,
    serviceIds: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);
  
  // Filters and pagination
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const limit = 20;

  const visibleServiceIds = useMemo(() => services.map((s) => s.id), [services]);
  const {
    selectedIds: selectedServiceIds,
    selectedCount: selectedCount,
    selectedArray: selectedServiceArray,
    clearSelection,
    selectAllCheckboxRef,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleServiceIds);

  useEffect(() => {
    fetchServices();
  }, [search, categoryFilter, statusFilter, sortKey, sortDir, page]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await fetch(getApiUrl(`/api/admin/services?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }

      const data: ServicesResponse = await response.json();
      
      if (data.success) {
        setServices(data.data.services);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        setError(data.message || 'Failed to load services');
      }
    } catch (error) {
      console.error('Fetch services error:', error);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (serviceId: string) => {
    navigate(`/services/${serviceId}/edit`);
  };

  const handleView = (serviceId: string) => {
    navigate(`/services/${serviceId}`);
  };

  const handleCreate = () => {
    navigate('/services/create');
  };

  const performBulkDelete = async () => {
    if (selectedServiceArray.length === 0) return;
    setBulkDeleting(true);
    const token = localStorage.getItem('token');
    let failed = 0;
    for (const id of selectedServiceArray) {
      try {
        const res = await fetch(getApiUrl(`/api/admin/services/${id}`), {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) failed++;
      } catch {
        failed++;
      }
    }
    setBulkDeleting(false);
    setBulkDeleteConfirm({ isOpen: false, serviceIds: [] });
    clearSelection();
    await fetchServices();
    if (failed > 0) setError(`${failed} suppression(s) en échec.`);
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedServiceArray.length === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, serviceIds: selectedServiceArray });
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, serviceIds: [] });
  const confirmBulkHardDelete = async () => {
    const ids = bulkHardDeleteConfirm.serviceIds;
    if (ids.length === 0) return;
    try {
      setBulkHardDeleting(true);
      const token = localStorage.getItem('token');
      let failed = 0;
      for (const id of ids) {
        try {
          const res = await fetch(getApiUrl(`/api/admin/services/${id}?hardDelete=true`), {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (!res.ok) failed++;
        } catch {
          failed++;
        }
      }
      setBulkHardDeleteConfirm({ isOpen: false, serviceIds: [] });
      clearSelection();
      await fetchServices();
      if (failed > 0) setError(`${failed} suppression(s) définitive(s) en échec.`);
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  const getCategoryBadge = (category: string) => {
    const colorMap: Record<string, string> = {
      'TATTOO': 'bg-purple-100 text-purple-800',
      'PIERCING': 'bg-pink-100 text-pink-800',
      'CONSULTATION': 'bg-blue-100 text-blue-800',
      'AFTERCARE': 'bg-green-100 text-green-800',
      'REMOVAL': 'bg-red-100 text-red-800',
      'JEWELRY': 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        colorMap[category] || 'bg-gray-100 text-gray-800'
      }`}>
        {categoryLabels[category] || category}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading && services.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des services...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
                <span>Gestion des Services</span>
                <HelpIcon 
                  docPath="services#liste" 
                  tooltip="Documentation : Liste des services"
                />
              </h1>
              <p className="text-textSecondary mt-1">
                {total} service{total !== 1 ? 's' : ''} au total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreate}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter un Service</span>
            </button>
            {selectedCount > 0 && (
              <div className="ml-3 flex items-center space-x-2">
                <button
                  onClick={() => setBulkDeleteConfirm({ isOpen: true, serviceIds: selectedServiceArray })}
                  disabled={bulkDeleting || bulkHardDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  <Trash className="w-4 h-4" />
                  <span>Supprimer ({selectedCount})</span>
                </button>
                {canHardDelete && (
                  <button
                    onClick={openBulkHardDeleteConfirm}
                    disabled={bulkDeleting || bulkHardDeleting}
                    className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                    title="Suppression définitive"
                  >
                    <AlertCircle className="w-4 h-4" />
                    <span>Suppression définitive ({selectedCount})</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un service..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les catégories</option>
              <option value="TATTOO">Tatouage</option>
              <option value="PIERCING">Piercing</option>
              <option value="CONSULTATION">Consultation</option>
              <option value="AFTERCARE">Soins</option>
              <option value="REMOVAL">Suppression</option>
              <option value="JEWELRY">Bijouterie</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch('');
              setCategoryFilter('');
              setStatusFilter('');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchServices}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Services Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    ref={selectAllCheckboxRef}
                    checked={selectedCount > 0 && selectedCount === services.length}
                    onChange={toggleSelectAllVisible}
                    className="rounded border-gray-300"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <Shield className="w-4 h-4" />
                    <span>Service</span>
                    {sortKey === 'name' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Catégorie</span>
                    {sortKey === 'category' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('basePrice')}
                >
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4" />
                    <span>Prix</span>
                    {sortKey === 'basePrice' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('estimatedDuration')}
                >
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>Durée</span>
                    {sortKey === 'estimatedDuration' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Réservation en ligne
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('isActive')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'isActive' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Créé le</span>
                    {sortKey === 'createdAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {services.map((service) => (
                <tr key={service.id} className="group hover:bg-gray-50">
                  <td className="px-4 py-4 w-10">
                    <input
                      type="checkbox"
                      checked={selectedServiceIds.has(service.id)}
                      onChange={() => toggleSelect(service.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {service.name}
                      </div>
                      {service.description && (
                        <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {service.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getCategoryBadge(service.category)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {service.basePrice.toFixed(2)}€
                    </div>
                    {service.depositRequired && (
                      <div className="text-xs text-orange-600">
                        Acompte requis
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {service.estimatedDuration} min
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      service.onlineBookingEnabled 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {service.onlineBookingEnabled ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(service.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(service.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleView(service.id)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded"
                        title="Voir le service"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(service.id)}
                        className="text-primary-600 hover:text-primary-700 p-1 rounded"
                        title="Modifier le service"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Empty State */}
        {!loading && services.length === 0 && (
          <div className="text-center py-12">
            <Shield className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun service trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || categoryFilter || statusFilter 
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Commencez par ajouter votre premier service.'
              }
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter un Service</span>
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(page - 1) * limit + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(page * limit, total)}</span> sur{' '}
                  <span className="font-medium">{total}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={bulkDeleteConfirm.isOpen}
        onClose={() => setBulkDeleteConfirm({ isOpen: false, serviceIds: [] })}
        onConfirm={performBulkDelete}
        title="Supprimer les services"
        message={`Êtes-vous sûr de vouloir supprimer ${bulkDeleteConfirm.serviceIds.length} service(s) ?`}
        confirmLabel="Supprimer"
        confirmVariant="danger"
        isLoading={bulkDeleting}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={bulkHardDeleteConfirm.serviceIds.length !== 1 ? 'service(s)' : 'service'}
        count={bulkHardDeleteConfirm.serviceIds.length}
        isLoading={bulkHardDeleting}
      />

      {/* Loading overlay for subsequent loads */}
      {loading && services.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}
    </div>
  );
}

